import BackToLogin from '../../components/BackToLogin';
import style from '../../components/Form.module.scss';
import Warnings from '../../components/Warning';
import { RecoveryPasswordService } from '../../services/RecoveryPasswordService';
import getPool from '../../UserPool';
import { DEFAULT_CLIENT_ID, DEFAULT_USER_POOL_ID } from '../../shared/Config';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const RESEND_COOLDOWN_SECONDS = 30;

const TITLE = 'Verifique sua caixa de entrada';

const DESCRIPTION =
  'Se o endereço existir em nossa base, enviamos um e-mail com um link para redefinir a senha. Abra-o e siga as instruções.';

const RESEND_ERROR =
  'Não foi possível reenviar o e-mail com o link. Tente novamente.';

export type RecoveryResponseType = 'email' | 'recoveryEmail';

export interface PasswordResetLinkSentProps {
  email: string;
  responseType: RecoveryResponseType;
}

/** Tela “link enviado” + reenvio com cooldown — paridade com oauth2_new password-reset-link-sent. */
function PasswordResetLinkSent({ email, responseType }: PasswordResetLinkSentProps) {
  const queryString = window.location.search;
  const urlParamsQs = new URLSearchParams(queryString);
  const params = Object.fromEntries(urlParamsQs.entries());
  const routeParams = useParams();

  const Pool = useMemo(() => {
    if (params.client_id) {
      return getPool(routeParams.pool_id || '', params.client_id);
    }
    return getPool(DEFAULT_USER_POOL_ID, DEFAULT_CLIENT_ID);
  }, [params.client_id, routeParams.pool_id]);

  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [isLoadingResend, setIsLoadingResend] = useState(false);
  const [error, setError] = useState(false);

  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const prevCountdownRef = useRef<number | null>(null);

  const clearCooldown = useCallback(() => {
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = undefined;
    }
  }, []);

  const startCooldown = useCallback(() => {
    clearCooldown();
    setResendDisabled(true);
    setCountdownSeconds(RESEND_COOLDOWN_SECONDS);
    cooldownIntervalRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        const next = prev - 1;
        return next <= 0 ? 0 : next;
      });
    }, 1000);
  }, [clearCooldown]);

  useEffect(() => {
    const prev = prevCountdownRef.current;
    prevCountdownRef.current = countdownSeconds;

    if (prev !== null && prev > 0 && countdownSeconds === 0) {
      clearCooldown();
      setResendDisabled(false);
    }
  }, [countdownSeconds, clearCooldown]);

  useEffect(() => {
    startCooldown();
    return () => clearCooldown();
  }, [startCooldown, clearCooldown]);

  const sendAgain = () => {
    const poolId = Pool.getUserPoolId();
    if (
      !email ||
      !poolId ||
      resendDisabled ||
      isLoadingResend
    ) {
      return;
    }

    setError(false);
    setIsLoadingResend(true);

    RecoveryPasswordService.sendCodeRecoveryEmail(
      { email, poolId },
      responseType,
    )
      .then(() => {
        setIsLoadingResend(false);
        startCooldown();
      })
      .catch(() => {
        setError(true);
        setIsLoadingResend(false);
      });
  };

  const resendBlocked = resendDisabled || isLoadingResend;

  return (
    <>
      <div className="mx-5 p-5 d-flex flex-column gap-4" style={{ minHeight: '100%' }}>
        <div
          className="d-flex flex-column gap-3 align-self-center"
          style={{ maxWidth: 400, width: '100%' }}
        >
          <div className="text-center d-flex flex-column gap-2">
            <span className="fw-semibold" id="pwd-reset-sent-title">
              {TITLE}
            </span>
            <p className="small mb-0" id="pwd-reset-sent-body">
              {DESCRIPTION}
            </p>
          </div>
          {error && (
            <div id="pwd-reset-sent-error">
              <Warnings warning={RESEND_ERROR} type="error" />
            </div>
          )}
        </div>

        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 w-100">
          <div className="d-flex flex-column align-items-center" style={{ maxWidth: 400, width: '100%' }}>
            <button
              type="button"
              id="resend-recovery-email-button"
              className={style.submitButton}
              disabled={resendBlocked}
              onClick={sendAgain}
            >
              {isLoadingResend ? 'Enviando…' : 'Reenviar e-mail'}
            </button>
            <div
              className="mt-3 text-center small w-100"
              style={{ minHeight: '2rem' }}
              aria-live="polite"
            >
              {resendDisabled && countdownSeconds > 0 && (
                <span id="pwd-reset-sent-cooldown">
                  Você pode pedir novo e-mail em {countdownSeconds} segundos.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={style.return} style={{ padding: '0em 0em 0em 1.5em' }}>
          <BackToLogin message="Voltar ao Login" url="/" />
        </div>
      </div>
    </>
  );
}

export default PasswordResetLinkSent;
